import { ConnectProto } from "@racoon/api";

type GovTruckInfo = ConnectProto.GovTruckInfo;

/**
 * One row of the desktop trucks table.
 *
 * Group headers and trucks share a single flat list because DataTable renders a
 * flat array: `renderCustomRow` swaps in the MVO header markup wherever
 * `_isGroupHeader` is set, and collapsed groups are filtered out of `data`
 * before it ever reaches the table.
 */
export type TruckRow = {
  /** Row key — `group:<providerId>` for headers, the truck id for trucks. */
  id: string;
  /** Absent on group-header rows. */
  truck?: GovTruckInfo;

  _isGroupHeader?: boolean;
  /** Set on every row (headers included) so a child can find its own group. */
  _groupKey: string;
  _groupProviderName: string;
  /** Trucks the registry lists for this MVO — what the group pill shows. */
  _groupTotalCount: number;
  /** Rounds off the bottom border of the last truck in a group. */
  _isLastInGroup?: boolean;
};

const UNKNOWN_PROVIDER_KEY = "unknown";

/**
 * Flattens trucks into `[MVO header, ...its trucks]` runs.
 *
 * Groups are ordered by truck count descending — the same order the provider
 * pie on the monitoring page uses — and trucks within a group by plate number,
 * which is the only field guaranteed to be present and human-meaningful.
 */
export function adaptTrucksByProvider(
  trucks: GovTruckInfo[],
  unknownProviderLabel: string,
): TruckRow[] {
  const groups = new Map<string, { providerName: string; trucks: GovTruckInfo[] }>();

  for (const info of trucks) {
    const providerId = info.serviceProvider?.id;
    const groupKey = providerId != null ? String(providerId) : UNKNOWN_PROVIDER_KEY;
    let group = groups.get(groupKey);
    if (!group) {
      group = { providerName: info.serviceProvider?.name || unknownProviderLabel, trucks: [] };
      groups.set(groupKey, group);
    }
    group.trucks.push(info);
  }

  const ordered = Array.from(groups.entries()).sort(([, a], [, b]) => {
    if (a.trucks.length !== b.trucks.length) return b.trucks.length - a.trucks.length;
    return a.providerName.localeCompare(b.providerName);
  });

  const rows: TruckRow[] = [];
  for (const [groupKey, group] of ordered) {
    const total = group.trucks.length;
    rows.push({
      id: `group:${groupKey}`,
      _isGroupHeader: true,
      _groupKey: groupKey,
      _groupProviderName: group.providerName,
      _groupTotalCount: total,
    });

    const sortedTrucks = [...group.trucks].sort((a, b) =>
      (a.truck?.plateNumber ?? "").localeCompare(b.truck?.plateNumber ?? ""),
    );

    sortedTrucks.forEach((info, index) => {
      rows.push({
        id: String(info.truck?.id ?? `${groupKey}:${index}`),
        truck: info,
        _groupKey: groupKey,
        _groupProviderName: group.providerName,
        _groupTotalCount: total,
        _isLastInGroup: index === sortedTrucks.length - 1,
      });
    });
  }

  return rows;
}
